import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { theme } from '../../styles/theme';
import { findPushScreen } from '../../config/pushScreens';
import { TTL_OPTIONS, findPushChannel } from '../../config/pushChannels';
import { customDataToObject } from '../../utils/pushOptions';
import { formatDateTime } from '../../services/pushMetrics';
import type { PushOptionsValue } from '../../types/push';

export interface ConfirmSendInfo {
  audienceLabel: string;
  users: number;
  devices: number;
  warnings: string[];
}

interface Props {
  info: ConfirmSendInfo | null;
  title: string;
  body: string;
  hasImage: boolean;
  options: PushOptionsValue;
  /** Horário escolhido no "Agendar", ou null para enviar agora. */
  scheduledAt: Date | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const plural = (count: number, singular: string, pluralWord: string) => `${count} ${count === 1 ? singular : pluralWord}`;

/** Resumo do que vai sair antes de enviar — substitui o window.confirm. */
const ConfirmSendDialog: React.FC<Props> = ({ info, title, body, hasImage, options, scheduledAt, onCancel, onConfirm }) => {
  const customData = Object.entries(customDataToObject(options.customData));
  const rows: [string, string][] = info
    ? [
        ['Público', `${info.audienceLabel} — ${plural(info.users, 'negócio', 'negócios')} · ${plural(info.devices, 'aparelho', 'aparelhos')}`],
        [
          'Quando',
          scheduledAt
            ? `Agendada para ${formatDateTime(scheduledAt)} (o público é recalculado na hora do envio)`
            : 'Agora',
        ],
        ['Notificação', `${title.trim()} — ${body.trim()}`],
        ['Imagem', hasImage ? 'Sim' : 'Sem imagem (só o ícone do app)'],
        ['Tela de destino', findPushScreen(options.screen)?.label || 'Nenhuma — só abre o app'],
        ['Som', options.sound ? 'Ligado' : 'Desligado'],
        ['Canal Android', findPushChannel(options.androidChannelId)?.label || options.androidChannelId],
        ['Validade', TTL_OPTIONS.find(option => option.value === options.ttlSeconds)?.label || `${options.ttlSeconds}s`],
        ...(customData.length
          ? [['Dados personalizados', customData.map(([key, value]) => `${key} = ${value}`).join(', ')] as [string, string]]
          : []),
        ...(options.name.trim() ? [['Nome interno', options.name.trim()] as [string, string]] : []),
      ]
    : [];

  return (
    <Dialog open={!!info} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle style={styles.title}>{scheduledAt ? 'Confirmar agendamento' : 'Confirmar envio'}</DialogTitle>
      <DialogContent>
        <table style={styles.table}>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td style={styles.labelCell}>{label}</td>
                <td style={styles.valueCell}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {info?.warnings.map(warning => (
          <div key={warning} style={styles.warning}>
            <WarningAmberIcon style={{ fontSize: 18, flexShrink: 0 }} />
            <span>{warning}</span>
          </div>
        ))}
      </DialogContent>
      <DialogActions style={styles.actions}>
        <button style={styles.cancelButton} onClick={onCancel}>Cancelar</button>
        <button style={styles.confirmButton} onClick={onConfirm}>{scheduledAt ? 'Agendar' : 'Enviar'}</button>
      </DialogActions>
    </Dialog>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  title: {
    color: theme.colors.primary,
    fontWeight: 'bold',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  labelCell: {
    padding: '8px 12px 8px 0',
    color: theme.colors.secondary,
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
    borderBottom: '1px solid #eee',
  },
  valueCell: {
    padding: '8px 0',
    color: theme.colors.text,
    wordBreak: 'break-word',
    borderBottom: '1px solid #eee',
  },
  warning: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
    marginTop: '14px',
    padding: '10px 14px',
    background: '#fff8e1',
    color: '#8a5300',
    borderRadius: '8px',
    fontSize: '13px',
  },
  actions: {
    padding: '0 24px 20px',
    gap: '10px',
  },
  cancelButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '2px solid #e0e0e0',
    background: 'white',
    color: theme.colors.text,
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  confirmButton: {
    padding: '10px 24px',
    borderRadius: '8px',
    border: 'none',
    background: theme.gradient,
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};

export default ConfirmSendDialog;
